import type { LngLat, Perimeter, PlanBuilding } from './types';

// ─── Géométrie du périmètre et des stations ────────────────────────────────

/** Polygone régulier autour d'un point, en lng/lat — approximation locale suffisante en Île-de-France. */
export function circlePolygon(center: LngLat, radiusKm: number, sides = 24): LngLat[] {
  const [lng, lat] = center;
  const dLat = radiusKm / 111.32;
  const dLng = radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180));
  return Array.from({ length: sides }, (_, i) => {
    const a = (2 * Math.PI * i) / sides;
    return [lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)] as LngLat;
  });
}

/** Distance approchée entre deux points, en kilomètres. */
export function distanceKm([x1, y1]: LngLat, [x2, y2]: LngLat): number {
  const dx = (x2 - x1) * 111.32 * Math.cos((((y1 + y2) / 2) * Math.PI) / 180);
  const dy = (y2 - y1) * 111.32;
  return Math.hypot(dx, dy);
}

/** Centre moyen d'un ensemble de points (centroïde grossier du périmètre). */
export function centerOf(points: LngLat[]): LngLat | null {
  if (points.length === 0) return null;
  const s = points.reduce((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0]);
  return [s[0] / points.length, s[1] / points.length];
}

/** Ray casting — anneau ouvert ou fermé, coordonnées lng/lat. */
export function pointInPolygon([x, y]: LngLat, ring: LngLat[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function inPerimeter(b: PlanBuilding, perimeter: Perimeter): boolean {
  if (perimeter.kind === 'communes') return perimeter.communes.includes(b.commune);
  return perimeter.polygon.length >= 3 && pointInPolygon(b.centroid, perimeter.polygon);
}

export function boundsOf(points: LngLat[]): [LngLat, LngLat] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of points) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return [[x0, y0], [x1, y1]];
}

export const closeRing = (ring: LngLat[]): LngLat[] => (ring.length ? [...ring, ring[0]] : ring);
